import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Calendar, Clock, MapPin, ArrowLeft, Share2, Wine, Shirt, Utensils, Car, Crown, Users, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, imageUrl, fullDate, money } from "@/lib/api";
import DateBadge from "@/components/DateBadge";

const INFO_ICONS = {
  age_requirement: ShieldCheck,
  dress_code: Shirt,
  food_available: Utensils,
  bar_available: Wine,
  parking_info: Car,
  vip_available: Crown,
  tables_available: Users,
};

export default function EventDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/events/${slug}`)
      .then((r) => {
        setEvent(r.data);
        document.title = `${r.data.name} — ABE Huntsville`;
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: event.name, url }); } catch (e) {}
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copied!");
    }
  };

  if (loading) return <div className="mx-auto max-w-3xl px-4 py-20 text-center text-zinc-500">Loading…</div>;
  if (notFound || !event)
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <p className="font-display text-4xl text-white uppercase">Event Not Found</p>
        <Link to="/events" className="btn-gold inline-block mt-6 rounded-md px-6 py-3 font-oswald tracking-widest text-sm">BROWSE EVENTS</Link>
      </div>
    );

  const s = event.settings || {};
  const sellable = !["sold_out", "cancelled", "coming_soon"].includes(event.status);
  const hasTickets = (event.tickets || []).some((t) => t.active);

  const infoItems = [];
  if (s.age_requirement) infoItems.push(["age_requirement", `${s.age_requirement} to Enter`]);
  if (s.dress_code) infoItems.push(["dress_code", s.dress_code]);
  if (s.food_available) infoItems.push(["food_available", "Food Available"]);
  if (s.bar_available) infoItems.push(["bar_available", "Full Bar Available"]);
  if (s.vip_available) infoItems.push(["vip_available", "VIP Available"]);
  if (s.tables_available) infoItems.push(["tables_available", "Tables Available"]);
  if (s.parking_info) infoItems.push(["parking_info", s.parking_info]);
  (event.info_lines || []).forEach((l) => infoItems.push(["age_requirement", l]));

  return (
    <div className="pb-28 md:pb-16">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 pt-4 flex items-center justify-between">
        <button data-testid="back-to-events" onClick={() => navigate("/events")} className="inline-flex items-center gap-1.5 font-oswald text-xs font-semibold tracking-widest text-zinc-300 hover:text-white">
          <ArrowLeft size={15} /> BACK TO EVENTS
        </button>
        <button data-testid="share-event" onClick={share} className="rounded-full border border-white/15 p-2 text-white hover:border-[#D4AF37] hover:text-[#D4AF37]">
          <Share2 size={16} />
        </button>
      </div>

      <div className="mx-auto max-w-3xl px-4 sm:px-6 mt-4">
        <div className="relative aspect-[4/3] sm:aspect-[16/9] overflow-hidden rounded-2xl border border-[#27272A]">
          <img src={imageUrl(event.cover_image || event.flyer_image)} alt={event.name} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute top-4 left-4"><DateBadge date={event.date} /></div>
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <span className="font-oswald text-xs tracking-widest text-[#D4AF37] uppercase">{event.category}</span>
            <h1 className="font-display text-4xl sm:text-5xl uppercase text-white leading-[0.9] mt-1">{event.name}</h1>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          <div className="flex items-center gap-3 text-zinc-200"><Calendar size={18} className="text-[#D4AF37]" /><span>{fullDate(event.date)}</span></div>
          <div className="flex items-center gap-3 text-zinc-200"><Clock size={18} className="text-[#D4AF37]" /><span>{event.start_time}{event.end_time ? ` – ${event.end_time}` : ""}</span></div>
          {event.venue && (
            <Link to={`/venues/${event.venue.slug}`} className="flex items-start gap-3 text-zinc-200 hover:text-white">
              <MapPin size={18} className="text-[#D4AF37] mt-0.5" />
              <span>{event.venue.name}{event.venue.address ? ` — ${event.venue.address}, ` : " — "}{event.venue.city}</span>
            </Link>
          )}
        </div>

        {sellable && hasTickets ? (
          <button data-testid="get-tickets-btn" onClick={() => navigate(`/events/${event.slug}/tickets`)} className="btn-gold w-full mt-6 rounded-lg py-4 font-oswald text-base tracking-widest">
            GET TICKETS {event.price_from != null && `— FROM ${money(event.price_from)}`}
          </button>
        ) : (
          <div className="w-full mt-6 rounded-lg border border-white/15 py-4 text-center font-oswald tracking-widest text-zinc-400">
            {event.status === "cancelled" ? "EVENT CANCELLED" : event.status === "coming_soon" ? "TICKETS COMING SOON" : "SOLD OUT"}
          </div>
        )}

        {event.description && (
          <div className="mt-8">
            <span className="section-eyebrow">Event Details</span>
            <p className="mt-3 text-zinc-300 leading-relaxed whitespace-pre-line">{event.description}</p>
          </div>
        )}

        {infoItems.length > 0 && (
          <div className="mt-7 space-y-3">
            {infoItems.map(([key, label], i) => {
              const Icon = INFO_ICONS[key] || ShieldCheck;
              return (
                <div key={i} className="flex items-center gap-3 text-zinc-200">
                  <Icon size={18} className="text-[#D4AF37]" /><span>{label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* sticky mobile CTA */}
      {sellable && hasTickets && (
        <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-[#121215]/95 backdrop-blur border-t border-[#27272A] p-4">
          <button data-testid="sticky-get-tickets" onClick={() => navigate(`/events/${event.slug}/tickets`)} className="btn-gold w-full rounded-lg py-4 font-oswald tracking-widest">
            GET TICKETS
          </button>
        </div>
      )}
    </div>
  );
}
